import './styles/graphic.scss';
import editorial from './data/editorial';
import {
  applyMode,
  joinSeats,
  keepLiveResults,
  loadLive,
  loadNational,
  louisianaScenario,
  resultMode,
  resultsOverride,
  urlParam,
} from './lib/feeds';
import { buildModel } from './lib/model';
import SECTION from './section';
import {
  draw, placeAimNote, seatTip, spaceAimClusters,
} from './lib/view';

export default function graphic(props, container) {
  const root = document.createElement('div');
  root.className = 'rd';
  container.node().appendChild(root);

  const ui = {
    whyOpen: false,
    showFlipped: false,
    openStates: {},
    statesOpen: false,
    districtState: 'ALL',
    districtOpen: false,
    narrow: false,
    preview: false,
    section: SECTION,
    mobileBar: urlParam('mbar') === 'vertical' ? 'vertical' : 'stack',
    laSeed: null,
    laPicks: [],
  };
  // A single-section embed sits flush in the CMS page, with no space around it.
  if (ui.section !== 'all') {
    root.classList.add('is-embed');
    // Transparent page, so the CMS article's background shows through the iframe.
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }
  const override = resultsOverride();
  ui.testing = !!override;
  // The 2024 results are bundled at build time, so only the 2026 feed is live.
  const { baseline } = editorial;
  let live = null;
  let failed = null;
  let fetching = false;
  let failures = 0;
  let latest = '';
  let national = null;
  let fetchingNational = false;
  let olderPolls = 0;
  let olderNational = 0;
  let liveKey = '';
  let drawnKey = null;
  const missingWarned = new Set();
  // After this many polls in a row that look older than the data on screen,
  // the feed is taken to have been reset and its data is shown anyway.
  const OLDER_LIMIT = 3;
  let width = props.initialWidth || container.node().clientWidth || 700;
  ui.loading = true;

  const handlers = {
    onToggleWhy: (open, panel) => {
      ui.whyOpen = open;
      followHeight(panel);
    },
    onToggleFlipped: () => {
      ui.showFlipped = !ui.showFlipped;
      render();
    },
    onToggleState: (code) => {
      ui.openStates[code] = !ui.openStates[code];
      render();
    },
    onToggleStates: () => {
      ui.statesOpen = !ui.statesOpen;
      render();
      if (!ui.statesOpen) scrollToSection('section.states');
    },
    onFilter: (code) => {
      ui.districtState = code;
      // A single state shows all its districts; "All" goes back to the short preview.
      ui.districtOpen = code !== 'ALL';
      render();
    },
    onShuffleLouisiana: () => {
      ui.laSeed = Math.floor(Math.random() * 1e9) + 1;
      render();
    },
    onResetLouisiana: () => {
      ui.laSeed = null;
      ui.laPicks = [];
      render();
    },
    onToggleDistricts: () => {
      ui.districtOpen = !ui.districtOpen;
      render();
      if (!ui.districtOpen) scrollToSection('section.districts');
    },
  };

  document.querySelectorAll('.rd-tip').forEach(node => node.remove());
  const tip = document.createElement('div');
  tip.className = 'rd-tip';
  tip.setAttribute('role', 'tooltip');
  document.body.appendChild(tip);
  let tipTarget = null;
  const seatsById = new Map();
  const TIP_TARGETS = '[data-seat]';
  function showTip(node) {
    if (node === tipTarget) return;
    const seat = seatsById.get(node.getAttribute('data-seat'));
    if (!seat) return;
    if (tipTarget) tipTarget.classList.remove('is-hover');
    tipTarget = node;
    node.classList.add('is-hover');
    tip.textContent = '';
    tip.appendChild(seatTip(seat));
    tip.classList.add('is-on');
    const box = node.getBoundingClientRect();
    const card = node.closest('.card, .state-card');
    const bounds = card ? card.getBoundingClientRect() : { left: 0, right: window.innerWidth, top: 0 };
    const half = tip.offsetWidth / 2;
    const minLeft = Math.max(bounds.left, 0) + half + 4;
    const maxLeft = Math.min(bounds.right, window.innerWidth) - half - 4;
    const left = Math.min(Math.max(box.left + (box.width / 2), minLeft), maxLeft);
    const below = box.top - 6 - tip.offsetHeight < Math.max(bounds.top, 0) + 4;
    tip.classList.toggle('is-below', below);
    tip.style.left = `${left}px`;
    tip.style.top = `${below ? box.bottom + 6 : box.top - 6}px`;
  }
  function hideTip() {
    if (tipTarget) tipTarget.classList.remove('is-hover');
    tipTarget = null;
    tip.classList.remove('is-on');
  }
  root.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const node = event.target.closest(TIP_TARGETS);
    if (node) showTip(node);
    else hideTip();
  });
  root.addEventListener('pointerleave', hideTip);
  root.addEventListener('click', (event) => {
    const node = event.target.closest(TIP_TARGETS);
    if (node && node !== tipTarget) showTip(node);
    else hideTip();
  });
  window.addEventListener('scroll', hideTip, { passive: true });

  const flipWarned = new Set();
  function warnFlipMismatches(seats) {
    seats.forEach((seat) => {
      if (!seat.called || seat.flipped === seat.isSwitch || flipWarned.has(seat.id)) return;
      flipWarned.add(seat.id);
      console.warn(`${seat.id}: 2024 winner ${seat.prevHolder}, 2026 winner ${seat.winner}, but the feed's isSwitch is ${seat.isSwitch}`);
    });
  }

  // Only the House section draws a bar; the others skip building one.
  function barLayout() {
    if (ui.section !== 'all' && ui.section !== 'house') return 'none';
    if (ui.narrow && ui.mobileBar === 'vertical') return 'vertical';
    return ui.narrow ? 'mobile' : 'desktop';
  }

  function currentModel() {
    if (!baseline || !live) return null;
    try {
      let seats = joinSeats(baseline, live, editorial);
      if (override) seats = applyMode(seats, override, keepLiveResults());
      // The Louisiana test button swaps in generated LA races on top of the
      // preview stage; the mode then follows the data, as it would on the night.
      const testing = override && ui.laSeed;
      if (testing) {
        const test = louisianaScenario(live, ui.laSeed);
        ui.laPicks = test.picks;
        const louisiana = new Map(joinSeats(baseline, test.live, editorial)
          .filter(seat => seat.state === 'LA')
          .map(seat => [seat.id, seat]));
        seats = seats.map(seat => louisiana.get(seat.id) || seat);
      }
      const mode = override && override !== 'early' && !testing ? override : resultMode(seats);
      const missing = seats.filter(seat => seat.targeted && !seat.margins).map(seat => seat.id);
      if (missing.length) console.warn('No presidential margins for', missing.join(', '));
      if (!override) warnFlipMismatches(seats);
      // Test stages mock their own counts, so the overall share follows them.
      const mocked = override && !keepLiveResults();
      const pctReporting = mocked
        ? seats.reduce((sum, seat) => sum + (Number(seat.pctReporting) || 0), 0) / seats.length
        : national && national.pctReporting;
      return buildModel(seats, editorial, {
        mode,
        pctReporting,
        showFlipped: ui.showFlipped,
        layout: barLayout(),
        width,
        updated: seats.reduce((latest, seat) => (seat.updated && seat.updated > latest ? seat.updated : latest), '') || null,
      });
    } catch (error) {
      failed = error;
      return null;
    }
  }

  // Waits a second frame so equalize() has sent the collapsed height first.
  function scrollToSection(selector) {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const node = root.querySelector(selector);
      if (!node) return;
      const top = node.getBoundingClientRect().top + window.pageYOffset - 12;
      if (window.pymChild && window.parent !== window) window.pymChild.scrollParentToChildPos(top);
      else if (node.getBoundingClientRect().top < 0) node.scrollIntoView({ block: 'start' });
    }));
  }

  function followHeight(panel) {
    if (!window.pymChild) return;
    const until = Date.now() + 800;
    let done = false;
    const finish = (event) => {
      if (event.target !== panel || event.propertyName !== 'grid-template-rows') return;
      done = true;
      panel.removeEventListener('transitionend', finish);
      window.pymChild.sendHeight();
    };
    if (panel) panel.addEventListener('transitionend', finish);
    const step = () => {
      window.pymChild.sendHeight();
      if (!done && Date.now() < until) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  function equalize() {
    // Card parts match heights within each row only, so a short row isn't padded out
    // to the tallest card in the grid. Tops go first, so the parts below them
    // are measured after the tops line up.
    const groups = ['[data-cardtop]', '[data-cardmid]'].map(selector => root.querySelectorAll(selector));
    groups.forEach(nodes => nodes.forEach((node) => {
      node.style.minHeight = '';
    }));
    if (!ui.narrow) {
      groups.forEach((nodes) => {
        const rows = new Map();
        nodes.forEach((node) => {
          const row = Math.round(node.getBoundingClientRect().top);
          if (!rows.has(row)) rows.set(row, []);
          rows.get(row).push(node);
        });
        rows.forEach((row) => {
          const max = Math.max(...row.map(node => node.offsetHeight));
          row.forEach((node) => {
            node.style.minHeight = `${max}px`;
          });
        });
      });
    }
    spaceAimClusters(root);
    placeAimNote(root);
    if (window.pymChild) window.pymChild.sendHeight();
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => window.requestAnimationFrame(equalize));
  }

  let noteWidths = '';
  const noteObserver = new ResizeObserver(() => {
    const cols = root.querySelector('.aim-cols');
    const column = root.querySelector('.aim-col-R');
    if (!cols || !column) return;
    const widths = `${cols.clientWidth}:${column.clientWidth}`;
    if (widths === noteWidths) return;
    noteWidths = widths;
    placeAimNote(root);
    if (window.pymChild) window.pymChild.sendHeight();
  });

  function watchNote() {
    noteObserver.disconnect();
    noteWidths = '';
    const cols = root.querySelector('.aim-cols');
    const column = root.querySelector('.aim-col-R');
    if (cols) noteObserver.observe(cols);
    if (column) noteObserver.observe(column);
  }

  function render() {
    const measurer = root.querySelector('.measurer');
    if (measurer && measurer.clientWidth > 40) width = measurer.clientWidth;
    ui.narrow = width < 600;
    const model = failed && !live ? null : currentModel();
    seatsById.clear();
    if (model) model.ordered.forEach(seat => seatsById.set(seat.id, seat));
    hideTip();
    draw(root, model, ui, handlers);
    watchNote();
    window.requestAnimationFrame(equalize);
  }

  const observer = new ResizeObserver(() => {
    const measurer = root.querySelector('.measurer') || root;
    const next = measurer.clientWidth || width;
    const narrow = next < 600;
    if (Math.abs(next - width) > 1 || narrow !== ui.narrow) {
      width = next;
      ui.narrow = narrow;
      render();
    }
  });

  // Everything a poll can change on screen, so an unchanged poll skips the redraw.
  function seatKey(seats) {
    return seats.map(seat => [
      seat.id, seat.extracted, seat.updated, seat.called, seat.winner, seat.leader, seat.runoff,
      seat.pctReporting, seat.candidates.map(candidate => `${candidate.votes}${candidate.winner ? 'w' : ''}${candidate.runoff ? 'r' : ''}`).join(','),
    ].join(':')).join('|');
  }

  function acceptLive(incoming) {
    const extracted = incoming.reduce((max, seat) => (seat.extracted && seat.extracted > max ? seat.extracted : max), '');
    // Ignore a response older than the data on screen (a slow request or a stale cache),
    // unless several in a row are older, which means the feed itself went back.
    if (latest && (!extracted || extracted < latest)) {
      olderPolls += 1;
      if (olderPolls < OLDER_LIMIT) return;
      console.warn(`The House feed was older than the data on screen for ${olderPolls} polls in a row; showing it anyway.`);
    }
    olderPolls = 0;
    const ids = new Set(incoming.map(seat => seat.id));
    const absent = baseline.filter(seat => !ids.has(seat.id) && !missingWarned.has(seat.id)).map(seat => seat.id);
    if (absent.length) {
      absent.forEach(id => missingWarned.add(id));
      console.warn('No race in the House feed for', absent.join(', '));
    }
    // A race missing from this poll keeps its last data instead of reverting to "no results".
    const kept = live ? live.filter(seat => !ids.has(seat.id)) : [];
    const next = kept.length ? incoming.concat(kept) : incoming;
    live = next;
    latest = extracted;
    liveKey = seatKey(next);
  }

  // Keeps the last good national share if a poll's is missing or older.
  function acceptNational(next) {
    if (!next || next.pctReporting == null) return false;
    if (national && national.extracted && (!next.extracted || next.extracted < national.extracted)) {
      olderNational += 1;
      if (olderNational < OLDER_LIMIT) return false;
    }
    olderNational = 0;
    const changed = !national || national.pctReporting !== next.pctReporting;
    national = next;
    return changed;
  }

  function update() {
    const key = [liveKey, national && national.pctReporting, ui.stale, ui.preview, !!failed, ui.loading].join('#');
    if (key === drawnKey) return;
    drawnKey = key;
    render();
  }

  // The national share is optional, so the seat results never wait for it.
  async function refreshNational() {
    if (fetchingNational) return;
    fetchingNational = true;
    const next = await loadNational();
    fetchingNational = false;
    if (acceptNational(next) && live) update();
  }

  async function refresh() {
    refreshNational();
    // Skip a poll while the last one is still loading.
    if (fetching) return;
    fetching = true;
    try {
      acceptLive(await loadLive());
      failed = null;
      failures = 0;
      ui.lastLoaded = new Date();
      ui.preview = false;
    } catch (error) {
      failures += 1;
      if (!live) {
        failed = error;
        if (override && baseline) {
          live = baseline.map(seat => ({
            state: seat.state,
            stateName: seat.stateName,
            district: seat.district,
            id: seat.id,
            called: true,
            winner: seat.winner,
            leader: null,
            pctReporting: null,
            candidates: [],
          }));
          liveKey = 'preview';
          ui.preview = true;
          failed = null;
        }
      }
    }
    fetching = false;
    // After two failed polls in a row, warn that results may be delayed.
    ui.stale = !!live && !ui.preview && failures >= 2;
    ui.loading = false;
    update();
  }

  const POLL = 30000;
  observer.observe(root);
  if (window.__rdTimer) window.clearInterval(window.__rdTimer);
  if (window.__rdTick) window.clearInterval(window.__rdTick);
  refresh();
  window.__rdTimer = window.setInterval(refresh, POLL);
}
